import React, { FC, useEffect, useRef } from 'react'

export type ARCapabilities =
  | 'IOS-QUICK-LOOK'
  | 'ANDROID-SCENE-VIEWER'
  | 'UNSUPPORTED'

type ARButtonProps = {
  iosSrc?: string
  imageId?: string
  arClickEvent?: (event_type: ARCapabilities) => void
  onInitialized?: (
    event_type: ARCapabilities,
    self?: HTMLButtonElement,
    activateAR?: (
      href: string,
      button: HTMLButtonElement,
      isQuickLook: boolean
    ) => void
  ) => void
}

const activateAR = (
  href: string,
  button: HTMLButtonElement,
  isQuickLook: boolean
) => {
  const anchor = document.createElement('a')
  if (isQuickLook) {
    anchor.appendChild(document.createElement('img'))
    anchor.rel = 'ar'
  }
  anchor.setAttribute('href', href)
  anchor.click()
  if (button && isQuickLook) {
    anchor.addEventListener('message', (event: any) => {
      if (event.data == '_apple_ar_quicklook_button_tapped') {
        button.dispatchEvent(new CustomEvent('quick-look-button-tapped'))
      }
    })
  }
}

const ARButton: FC<
  ARButtonProps &
  React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  >
> = ({ iosSrc, imageId, arClickEvent, onInitialized, children, ...props }) => {
  const arButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!arButtonRef.current) return

    const IS_ANDROID = /android/i.test(navigator.userAgent)
    const IS_IOS =
      //@ts-ignore
      (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

    const IS_SAFARI = /Safari\//.test(navigator.userAgent)
    const IS_FIREFOX = /firefox/i.test(navigator.userAgent)
    const IS_OCULUS = /OculusBrowser/.test(navigator.userAgent)
    const IS_IOS_CHROME = IS_IOS && /CriOS\//.test(navigator.userAgent)
    const IS_IOS_SAFARI = IS_IOS && IS_SAFARI

    const SUPPORTS_SCENEVIEWER = IS_ANDROID && !IS_FIREFOX && !IS_OCULUS
    const SUPPORTS_QUICKLOOK = (() => {
      const anchor = document.createElement('a')
      return (
        anchor.relList &&
        anchor.relList.supports &&
        anchor.relList.supports('ar')
      )
    })()

    if (arButtonRef.current.getAttribute('ar') != null) return
    if ((IS_IOS_CHROME || IS_IOS_SAFARI) && SUPPORTS_QUICKLOOK) {
      arButtonRef.current.setAttribute('ar', 'quick-look')
      arButtonRef.current.dispatchEvent(
        new CustomEvent('initialized', { detail: 'quick-look' })
      )
      if (onInitialized)
        onInitialized('IOS-QUICK-LOOK', arButtonRef.current, activateAR)
      arButtonRef.current.addEventListener('click', () => {
        if (arClickEvent) arClickEvent('IOS-QUICK-LOOK')
        activateAR(`${iosSrc}#`, arButtonRef.current!, true)
      })
    } else if (SUPPORTS_SCENEVIEWER) {
      arButtonRef.current.setAttribute('ar', 'scene-viewer')
      arButtonRef.current.dispatchEvent(
        new CustomEvent('initialized', { detail: 'scene-viewer' })
      )
      if (onInitialized)
        onInitialized('ANDROID-SCENE-VIEWER', arButtonRef.current, activateAR)
      arButtonRef.current.addEventListener('click', () => {
        if (arClickEvent) arClickEvent('ANDROID-SCENE-VIEWER')

        // https://unpkg.com/@leoncvlt/ar-button@0.1.2/index.js
        // Android src.....
      })
    } else {
      arButtonRef.current.setAttribute('ar', 'unsupported')
      arButtonRef.current.dispatchEvent(
        new CustomEvent('initialized', { detail: 'unsupported' })
      )
      if (onInitialized)
        onInitialized('UNSUPPORTED', arButtonRef.current, activateAR)
      arButtonRef.current.addEventListener('click', () => {
        if (arClickEvent) arClickEvent('UNSUPPORTED')
      })
    }
  }, [arButtonRef])

  return (
    <button type='button' {...props} data-ios-src={iosSrc} ref={arButtonRef}>
      {children}
    </button>
  )
}

export default ARButton
